import { useCallback, useEffect, useState } from 'react';
import { userAtom } from '@/global-store';
import { Collapse } from '@mui/material';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { allowedToShowItem } from '@/helpers/allowedToShowItem.js';
import NavItem from '@layout/Sidebar/MenuList/NavItem.tsx';
import { useSidebar } from '@layout/Sidebar/SidebarContext.ts';
import SidebarNavButton from '@layout/Sidebar/MenuList/SidebarNavButton.tsx';
import NavItemLabel from '@layout/Sidebar/MenuList/NavItemLabel.tsx';
import { useNavigate } from 'react-router-dom';
import type { MenuItem } from '@layout/Sidebar/MenuList/types.ts';

type NavCollapseProps = {
  item: MenuItem;
  level: number;
};

function NavCollapse({ item, level }: NavCollapseProps) {
  const [user] = useAtom(userAtom);

  const { isMobile } = useSidebar();

  const defaultExpanded = item.handle?.defaultExpanded || false;

  const [open, setOpen] = useState(defaultExpanded);

  const { t } = useTranslation();

  const { pathname } = useLocation();

  const navigate = useNavigate();

  const handleClick = () => {
    if (open) {
      setOpen(false);
    } else {
      setOpen(true);

      // For desktop, we're automatically navigating to the first subitem
      if (!isMobile && item.children?.[0]?.path) {
        navigate(item.children[0].path);
      }
    }
  };

  const matchesCurrentPath = useCallback(
    (child: MenuItem) =>
      child.path && pathname.includes(child.path.replace(/\/:[^]*/g, '')),
    [pathname],
  );

  const hasMatchingChild = useCallback(
    function inner(children?: MenuItem[]): boolean {
      if (!children?.length) {
        return false;
      }

      return children.some(
        (child) => matchesCurrentPath(child) || inner(child.children),
      );
    },
    [matchesCurrentPath],
  );

  useEffect(() => {
    if (defaultExpanded) {
      setOpen(true);
      return;
    }

    setOpen(hasMatchingChild(item.children));
  }, [defaultExpanded, hasMatchingChild, item.children]);

  const title = item.handle?.i18nNameKey
    ? t(item.handle.i18nNameKey)
    : (item.handle?.name ?? '');

  // menu collapse & item
  const isVisibleChild = (child: MenuItem) => {
    const handle = child.handle;
    if (handle?.type === 'hidden') {
      return false;
    }

    const allowed = handle?.allowed;
    if (!allowed) {
      return true;
    }

    const permissions = Array.isArray(allowed) ? allowed : [allowed];
    return allowedToShowItem(permissions, user, child);
  };

  const menus = item.children?.filter(isVisibleChild) ?? [];

  return (
    <>
      <SidebarNavButton
        component='button'
        level={level}
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={'nav-collapse-' + String(title).replaceAll(' ', '-')}
        onClick={handleClick}
        tooltip={
          item.handle?.i18nTooltipKey
            ? t(item.handle.i18nTooltipKey)
            : undefined
        }
      >
        <NavItemLabel item={item} title={title} />

        <div className='eye:ml-auto'>
          {open ? <IconChevronUp /> : <IconChevronDown />}
        </div>
      </SidebarNavButton>
      <Collapse
        id={'nav-collapse-' + String(title).replaceAll(' ', '-')}
        in={open}
        timeout='auto'
        unmountOnExit
      >
        <ul>
          {menus.map((child) => (
            <li key={child.path}>
              <NavItem
                item={child}
                level={level + 1}
                collapsibleTitle={title}
              />
            </li>
          ))}
        </ul>
      </Collapse>
    </>
  );
}

export default NavCollapse;
